import { instance } from "api/apiBase";
import {
  CreateUnderwritingRulePayload,
  SaveTemplateRulesPayload,
  UnderwritingAgent,
  UnderwritingRule,
  UnderwritingRuleDefault,
  UpdateUnderwritingRulePayload
} from "./types";

// Underwriting agents share the AI agents endpoints with the other agent types;
// only their rules have endpoints of their own.
const BASE = "/portal/v1/agents";
const TYPE = "underwriting_agent";

export const UNDERWRITING_AGENTS_QUERY_KEY = ["UNDERWRITING_AGENTS"];
export const underwritingAgentRulesQueryKey = (agentId: number | string) => [
  ...UNDERWRITING_AGENTS_QUERY_KEY,
  String(agentId),
  "RULES"
];
export const UNDERWRITING_RULE_DEFAULTS_QUERY_KEY = [
  ...UNDERWRITING_AGENTS_QUERY_KEY,
  "DEFAULTS"
];
export const underwritingTemplateRulesQueryKey = (
  agentId: number,
  templateId: string
) => [...underwritingAgentRulesQueryKey(agentId), "TEMPLATE", templateId];

const rulesPath = (agentId: number) => `${BASE}/${agentId}/underwriting_rules`;

export const underwritingAgentsApi = {
  list: async (): Promise<UnderwritingAgent[]> => {
    const { data } = await instance.get<UnderwritingAgent[]>(BASE, {
      params: { type: TYPE }
    });
    return data;
  },

  listRules: async (agentId: number): Promise<UnderwritingRule[]> => {
    const { data } = await instance.get<UnderwritingRule[]>(rulesPath(agentId));
    return data;
  },

  listTemplateRules: async (
    agentId: number,
    templateId: string
  ): Promise<UnderwritingRule[]> => {
    const { data } = await instance.get<UnderwritingRule[]>(
      rulesPath(agentId),
      {
        params: { verification_template_identifier: templateId }
      }
    );
    return data;
  },

  saveTemplateRules: async ({
    agentId,
    ...payload
  }: SaveTemplateRulesPayload): Promise<UnderwritingRule[]> => {
    const { data } = await instance.put<UnderwritingRule[]>(
      rulesPath(agentId),
      payload
    );
    return data;
  },

  listDefaults: async (): Promise<UnderwritingRuleDefault[]> => {
    const { data } = await instance.get<UnderwritingRuleDefault[]>(
      `${BASE}/underwriting_rules/defaults`
    );
    return data;
  },

  restoreDefaults: async ({
    agentId,
    default_keys
  }: {
    agentId: number;
    default_keys: string[];
  }): Promise<UnderwritingRule[]> => {
    const { data } = await instance.post<UnderwritingRule[]>(
      `${rulesPath(agentId)}/restore_defaults`,
      { default_keys }
    );
    return data;
  },

  createRule: async ({
    agentId,
    ...payload
  }: CreateUnderwritingRulePayload): Promise<UnderwritingRule> => {
    const { data } = await instance.post<UnderwritingRule>(
      rulesPath(agentId),
      payload
    );
    return data;
  },

  updateRule: async ({
    agentId,
    ruleId,
    ...payload
  }: UpdateUnderwritingRulePayload): Promise<UnderwritingRule> => {
    const { data } = await instance.patch<UnderwritingRule>(
      `${rulesPath(agentId)}/${ruleId}`,
      payload
    );
    return data;
  },

  removeRule: async ({
    agentId,
    ruleId
  }: {
    agentId: number;
    ruleId: number;
  }): Promise<void> => {
    await instance.delete(`${rulesPath(agentId)}/${ruleId}`);
  }
};
